import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Facebook, Share2, Link2, MessageCircle, MessageSquare, MapPin, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import CommunityPostCard from '../components/community/CommunityPostCard';
import BackToTop from '../components/BackToTop';
import { tinyThumbnail } from '../utils/cloudinaryOptimize';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';
const SITE_URL = 'https://discoveressex.co.uk';

const stripHtml = (str) => {
  if (!str) return '';
  const txt = str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  const el = typeof document !== 'undefined' ? document.createElement('textarea') : null;
  if (el) { el.innerHTML = txt; return el.value; }
  return txt.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#039;/g, "'");
};

const POST_TYPE_META = {
  general: { label: 'General' }, question: { label: 'Question' }, recommendation: { label: 'Recommendation' },
  poll: { label: 'Poll' }, photo: { label: 'Photo' }, 'lost-found': { label: 'Lost & Found' },
  traffic: { label: 'Traffic Alert' }, alert: { label: 'Alert' }, 'crime-safety': { label: 'Crime & Safety' },
  'parenting-family': { label: 'Parenting / Family' }, 'pets-animals': { label: 'Pets & Animals' },
  'food-drink': { label: 'Food & Drink' }, 'home-garden': { label: 'Home & Garden' },
  'events-whats-on': { label: "Events & What's On" }, 'weather-travel': { label: 'Weather & Travel' },
  'business-shoutouts': { label: 'Business Shout-outs' }, 'for-sale-free-wanted': { label: 'For Sale / Free / Wanted' },
  'local-history': { label: 'Local History' }, 'rants-raves': { label: 'Rants & Raves' },
};

const CommunityPostPage = () => {
  const { postId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [adjacentPosts, setAdjacentPosts] = useState({ prev: null, next: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      // Auth handled by httpOnly cookie
      const res = await axios.get(`${API}/community/posts/${postId}`);
      setPost(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Post not found or has been removed.' : 'Failed to load post.');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => { fetchPost(); }, [fetchPost]);

  // Fetch adjacent post IDs for prev/next navigation
  useEffect(() => {
    const fetchAdjacent = async () => {
      try {
        const res = await axios.get(`${API}/community/posts?page=1&limit=100`);
        const posts = res.data.posts || [];
        const idx = posts.findIndex(p => p.id === postId);
        if (idx === -1) return;
        const prev = idx > 0 ? posts[idx - 1] : null;
        const next = idx < posts.length - 1 ? posts[idx + 1] : null;
        setAdjacentPosts({ prev, next });
      } catch {}
    };
    fetchAdjacent();
  }, [postId]);

  // Fetch related posts from same type/town
  useEffect(() => {
    if (!post) return;
    const fetchRelated = async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '4' });
        if (post.type) params.append('type', post.type);
        else if (post.town) params.append('town', post.town);
        const res = await axios.get(`${API}/community/posts?${params}`);
        setRelatedPosts((res.data.posts || []).filter(p => p.id !== postId).slice(0, 3));
      } catch {}
    };
    fetchRelated();
  }, [post, postId]);

  const shareUrl = `${SITE_URL}/community/post/${postId}`;
  const shareText = post ? stripHtml(post.title || (post.content || '').slice(0, 100)) : '';

  const shareLinks = [
    { name: 'Facebook', icon: Facebook, color: 'bg-[#1877F2] hover:bg-[#166FE5]', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { name: 'WhatsApp', icon: MessageCircle, color: 'bg-[#25D366] hover:bg-[#20BD5A]', url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}` },
    { name: 'X', icon: Share2, color: 'bg-black hover:bg-gray-800', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}` },
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center px-4">
      <div className="text-center space-y-4">
        <p className="text-lg text-slate-600">{error}</p>
        <Link to="/community" className="inline-flex items-center gap-2 text-sm text-[#0F2C3E] font-medium hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Community Hub
        </Link>
      </div>
    </div>
  );

  const displayName = post.display_name || 'Community Member';
  const postType = post.type || 'general';
  const categoryLabel = (POST_TYPE_META[postType] || { label: 'General' }).label;
  const cleanContent = stripHtml(post.content || '');
  const cleanTitle = stripHtml(post.title || '') || cleanContent.slice(0, 60);

  return (
    <div className="min-h-screen bg-[#F8F7F4]" data-testid="community-post-page">
      <SEO
        title={`${cleanTitle} | ${displayName} in ${categoryLabel}`}
        description={`${displayName} posted in ${categoryLabel}: ${cleanContent.slice(0, 160)}`}
        url={shareUrl}
        canonicalUrl={shareUrl}
        type="article"
        image={(post.images && post.images[0]) || undefined}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "DiscussionForumPosting",
          "headline": cleanTitle.slice(0, 80),
          "text": cleanContent,
          "author": { "@type": "Person", "name": displayName },
          "datePublished": post.created_at,
          "url": shareUrl,
          "discussionUrl": `${SITE_URL}/community`,
          "interactionStatistic": [
            { "@type": "InteractionCounter", "interactionType": "https://schema.org/CommentAction", "userInteractionCount": post.comments_count || 0 },
            { "@type": "InteractionCounter", "interactionType": "https://schema.org/LikeAction", "userInteractionCount": post.likes_count || 0 }
          ],
          ...(post.images && post.images[0] ? { "image": post.images[0] } : {})
        }}
        breadcrumbs={[
          { name: 'Home', url: SITE_URL },
          { name: 'Community', url: `${SITE_URL}/community` },
          { name: cleanTitle.slice(0, 40) || 'Post', url: shareUrl }
        ]}
      />

      <div className="container mx-auto px-4 md:px-6 py-8 max-w-3xl">
        {/* Back link */}
        <Link to="/community" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0F2C3E] mb-6 transition-colors" data-testid="back-to-community">
          <ArrowLeft className="h-4 w-4" /> Back to Community Hub
        </Link>

        {/* Post — show comments expanded by default for SEO depth */}
        <CommunityPostCard
          post={post}
          currentUser={user}
          onReactionChange={fetchPost}
          onDelete={fetchPost}
          defaultShowComments
        />

        {/* Share bar */}
        <div className="mt-6 p-5 bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="share-bar">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Share this post</h3>
          <div className="flex flex-wrap gap-2">
            {shareLinks.map(({ name, icon: Icon, color, url }) => (
              <a
                key={name}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium transition-colors ${color}`}
                data-testid={`share-${name.toLowerCase()}`}
              >
                <Icon className="h-4 w-4" /> {name}
              </a>
            ))}
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors"
              data-testid="share-copy-link"
            >
              <Link2 className="h-4 w-4" /> Copy Link
            </button>
          </div>
        </div>

        {/* Facebook Group CTA */}
        <div className="mt-6 p-5 bg-gradient-to-r from-[#1877F2]/5 to-[#1877F2]/10 rounded-2xl border border-[#1877F2]/20" data-testid="fb-group-cta-post">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#1877F2] flex items-center justify-center shrink-0">
              <Facebook className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800">Share to our Facebook Group</p>
              <p className="text-xs text-slate-500 mt-0.5">Share with our growing Essex community</p>
            </div>
            <a
              href={`https://www.facebook.com/groups/discoveressexuk/?multi_permalinks=&ref=share&post_text=${encodeURIComponent(shareText + '\n\nRead more: ' + shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-[#1877F2] hover:bg-[#166FE5] text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap"
              data-testid="share-fb-group"
            >
              Share to Group
            </a>
          </div>
        </div>

        {/* Prev / Next navigation */}
        {(adjacentPosts.prev || adjacentPosts.next) && (
          <div className="mt-6 flex items-stretch gap-3" data-testid="post-navigation">
            {adjacentPosts.prev ? (
              <button
                onClick={() => navigate(`/community/post/${adjacentPosts.prev.id}`)}
                className="flex-1 flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all text-left group"
                data-testid="prev-post-btn"
              >
                <ChevronLeft className="h-5 w-5 text-slate-300 group-hover:text-[#0F2C3E] shrink-0 transition-colors" />
                <div className="min-w-0">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Previous</span>
                  <p className="text-xs text-slate-600 group-hover:text-[#0F2C3E] line-clamp-2 mt-0.5 transition-colors">{stripHtml(adjacentPosts.prev.content || '').slice(0, 80)}</p>
                </div>
              </button>
            ) : <div className="flex-1" />}
            {adjacentPosts.next ? (
              <button
                onClick={() => navigate(`/community/post/${adjacentPosts.next.id}`)}
                className="flex-1 flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all text-right group"
                data-testid="next-post-btn"
              >
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Next</span>
                  <p className="text-xs text-slate-600 group-hover:text-[#0F2C3E] line-clamp-2 mt-0.5 transition-colors">{stripHtml(adjacentPosts.next.content || '').slice(0, 80)}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-[#0F2C3E] shrink-0 transition-colors" />
              </button>
            ) : <div className="flex-1" />}
          </div>
        )}

        {/* Related Posts — adds internal linking + content depth for SEO */}
        {relatedPosts.length > 0 && (
          <div className="mt-8" data-testid="related-posts">
            <h2 className="text-base font-semibold text-[#0F2C3E] mb-4">More from {categoryLabel}</h2>
            <div className="space-y-3">
              {relatedPosts.map(rp => (
                <Link
                  key={rp.id}
                  to={`/community/post/${rp.id}`}
                  className="block p-4 bg-white rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all"
                  data-testid={`related-post-${rp.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0F2C3E] to-[#1a4a6b] flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-white">{((rp.display_name || rp.user_name || 'A'))[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 line-clamp-2">{stripHtml(rp.content || '')}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{rp.town}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(rp.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                        {rp.comments_count > 0 && <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{rp.comments_count}</span>}
                      </div>
                    </div>
                    {rp.images && rp.images[0] && (
                      <img src={tinyThumbnail(rp.images[0])} alt={`${rp.title || 'Related post'} thumbnail`} className="w-14 h-14 rounded-lg object-cover shrink-0" loading="lazy" />
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <BackToTop />
    </div>
  );
};

export default CommunityPostPage;
